import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutGrid, Package, Palette, FlaskConical, Sparkles, MessageSquare, Users, Boxes, LogOut, ChevronRight, MapPin, Globe, Milestone, Beaker, Lightbulb, Heart, Store, ToggleLeft, BookOpen, AlertCircle, IndianRupee, Image as ImageIcon, Zap, CalendarRange, Thermometer, Award, Gift, Coins, ScanLine, RotateCcw, Route, Lock, CreditCard, Wallet } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/constants/brand";

// Items visible to STAFF (limited daily-use only). Master sees everything.
const STAFF_PATHS = new Set(["/admin/cash"]);

const ITEMS = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutGrid },
  { to: "/admin/cash", label: "Cash Control", icon: Wallet, staff: true, top: true },
  { to: "/admin/wishlist", label: "Community Wishlist", icon: Package },
  { to: "/admin/sections", label: "Section Manager", icon: ToggleLeft },
  { to: "/admin/features", label: "Feature Toggles", icon: Zap },
  { to: "/admin/campaigns", label: "Seasonal Campaigns", icon: CalendarRange },
  { to: "/admin/heat-rules", label: "Heat Recommendations", icon: Thermometer },
  { to: "/admin/assets", label: "Image Manager", icon: ImageIcon },
  { to: "/admin/worlds", label: "Category Worlds", icon: Globe },
  { to: "/admin/journey", label: "Growth Journey", icon: Milestone },
  { to: "/admin/lab", label: "Flavour Lab", icon: Beaker },
  { to: "/admin/challenges", label: "Challenges", icon: Lightbulb },
  { to: "/admin/contributors", label: "Contributors", icon: Heart },
  { to: "/admin/journal", label: "Journal", icon: BookOpen },
  { to: "/admin/complaints", label: "Complaints & Feedback", icon: AlertCircle },
  { to: "/admin/invoices", label: "Invoices & Payments", icon: IndianRupee },
  { to: "/admin/members", label: "Members", icon: Award },
  { to: "/admin/rewards", label: "Rewards Catalogue", icon: Gift },
  { to: "/admin/coin-rules", label: "Coin Rules", icon: Coins },
  { to: "/admin/scanner", label: "In-Store Scanner", icon: ScanLine },
  { to: "/admin/spin-wheels", label: "Spin & Win", icon: RotateCcw },
  { to: "/admin/loyalty", label: "Loyalty Journey", icon: Route },
  { to: "/admin/vault", label: "Community Vault", icon: Lock },
  { to: "/admin/pay-scratch", label: "Pay & Scratch", icon: CreditCard },
  { to: "/admin/retailers", label: "Retailers", icon: Store },
  { to: "/admin/demand", label: "Demand & Stock", icon: MapPin },
  { to: "/admin/submissions", label: "Inventions", icon: Sparkles },
  { to: "/admin/prototypes", label: "Prototypes", icon: FlaskConical },
  { to: "/admin/reviews", label: "Reviews", icon: MessageSquare },
  { to: "/admin/leads", label: "Leads & Orders", icon: ChevronRight },
  { to: "/admin/crm", label: "CRM", icon: Users },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/flavors", label: "Flavors", icon: Palette },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
];

export default function AdminLayout() {
  const { user, logout, isStaff } = useAuth();
  const nav = useNavigate();
  const doLogout = () => { logout(); nav("/admin/login"); };
  const staff = isStaff();
  const visibleItems = staff ? ITEMS.filter((it) => it.staff) : ITEMS;

  return (
    <div data-testid="admin-layout" data-role={user?.role_level || "master"} className="min-h-screen grid lg:grid-cols-[260px_1fr] bg-[#FFF8F0]">
      <aside className="border-r border-brand-line bg-white">
        <div className="p-6 flex items-center justify-between">
          <Link to={staff ? "/admin/cash" : "/"} className="flex items-center gap-2" data-testid="admin-home-link">
            <img src={BRAND.logoUrl} alt="Pop Panda" className="h-9 w-9 object-contain" />
            <div className="font-display text-xl leading-none">Pop Panda</div>
          </Link>
        </div>
        <nav className="px-3 py-2 space-y-1">
          {visibleItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`admin-nav-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${
                  isActive ? "bg-brand-charcoal text-brand-cream" : "text-brand-charcoal/75 hover:bg-brand-muted"
                } ${it.top ? "ring-1 ring-brand-yellow/40" : ""}`
              }
            >
              <it.icon size={16} /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-6 left-6 right-6 max-w-[210px]">
          <div className="text-xs text-brand-charcoal/55">Signed in as</div>
          <div className="text-sm font-medium truncate" data-testid="admin-current-user">{user?.login_id || user?.email}</div>
          <div className="text-[10px] uppercase tracking-widest mt-0.5 inline-block px-2 py-0.5 rounded-full bg-brand-yellow/25 text-brand-charcoal">{user?.role_level || "master"}</div>
          <button data-testid="admin-logout" onClick={doLogout} className="mt-3 btn-pill btn-ghost text-xs w-full justify-start">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="p-8 lg:p-12 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
